import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Tag, Popconfirm, message } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { embotAPI } from '../../services/api';

const { TextArea } = Input;

export default function EMTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await embotAPI.listTemplates();
      setTemplates(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await embotAPI.updateTemplate(editingId, values);
        message.success('Template updated');
      } else {
        await embotAPI.createTemplate(values);
        message.success('Template created');
      }
      setModalOpen(false);
      form.resetFields();
      setEditingId(null);
      fetch();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    await embotAPI.deleteTemplate(id);
    message.success('Deleted');
    fetch();
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', width: 200, ellipsis: true },
    { title: 'Subject', dataIndex: 'subject', width: 250, ellipsis: true },
    { title: 'Category', dataIndex: 'category', width: 120, render: v => v ? <Tag>{v}</Tag> : '-' },
    {
      title: 'Variables', dataIndex: 'variables', width: 200,
      render: vars => (vars || []).map(v => <Tag key={v} color="geekblue">{`{{${v}}}`}</Tag>),
    },
    {
      title: 'Created', dataIndex: 'created_at', width: 150,
      render: v => v ? new Date(v).toLocaleDateString('zh-TW') : '-',
    },
    {
      title: 'Actions', width: 100, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>
          New Template
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={templates}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1000 }}
      />

      <Modal
        title={editingId ? 'Edit Template' : 'New Template'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditingId(null); form.resetFields(); }}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Template Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="subject" label="Subject"><Input placeholder="Use {{variable}} for dynamic content" /></Form.Item>
          <Form.Item name="category" label="Category">
            <Select allowClear placeholder="Select category" options={[
              { value: 'promotion', label: 'Promotion' },
              { value: 'follow-up', label: 'Follow-up' },
              { value: 'newsletter', label: 'Newsletter' },
              { value: 'notification', label: 'Notification' },
            ]} />
          </Form.Item>
          <Form.Item name="body_html" label="Body (HTML)">
            <TextArea rows={8} placeholder="<h1>Hello {{name}}</h1><p>Content...</p>" />
          </Form.Item>
          <Form.Item name="variables" label="Variables">
            <Select mode="tags" placeholder="Type variable names (e.g. name, company)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
